import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { getRoute } from '../actions/sessions';
import {
  baselineCreditLimit,
  continuationTarget,
  isAmbiguousSkipError,
  skipEventProperties,
} from '../lib/utils/post_approval_plaid_skip';
import {
  capturePostApprovalPlaidEvent,
  POST_APPROVAL_PLAID_EVENTS,
} from '../lib/utils/post_approval_plaid_events';
import usePostApprovalPlaidContext from './usePostApprovalPlaidContext';

const usePostApprovalPlaidSkip = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const session = useSelector((state) => state.sessions.get('session'));
  const company = useSelector((state) =>
    state.entities.getIn(['company', session.get('company_id')])
  );
  const {
    attempt,
    contextManaged,
    error,
    hasAttempt,
    loading,
    phase,
    preAttemptCanContinue,
    refreshEntities,
    refreshStatus,
    skip,
  } = usePostApprovalPlaidContext();
  const [skipState, setSkipState] = useState('idle');
  const [attemptContextKey, setAttemptContextKey] = useState(null);
  const skipInFlight = useRef(null);
  const emittedAttemptIds = useRef(new Set());
  const mounted = useRef(false);
  const attemptRef = useRef(attempt);
  const staleAttempt = useRef(attempt);
  attemptRef.current = attempt;
  const sessionId = session.get('id');
  const applicationId = session.get('application_id');
  const publicKey = useSelector((state) => state.sessions.get('public_key'));
  const contextKey = JSON.stringify([sessionId, publicKey, applicationId]);
  const contextKeyRef = useRef(contextKey);
  contextKeyRef.current = contextKey;

  const isCurrentContext = useCallback(
    (key) => mounted.current && contextKeyRef.current === key,
    []
  );

  useEffect(() => {
    mounted.current = true;
    skipInFlight.current = null;
    emittedAttemptIds.current = new Set();
    staleAttempt.current = attemptRef.current;
    setAttemptContextKey(contextManaged ? contextKey : null);
    setSkipState('idle');

    return () => {
      mounted.current = false;
    };
  }, [contextKey, contextManaged]);

  useEffect(() => {
    if (attempt !== staleAttempt.current) {
      setAttemptContextKey(contextKey);
    }
  }, [attempt, contextKey]);

  const emitSkipped = useCallback(
    (skippedAttempt, result) => {
      const attemptId = skippedAttempt?.get('attempt_id');
      if (!attemptId || emittedAttemptIds.current.has(attemptId)) return;

      emittedAttemptIds.current.add(attemptId);
      capturePostApprovalPlaidEvent(POST_APPROVAL_PLAID_EVENTS.SKIPPED, {
        session,
        attempt: skippedAttempt,
        ...skipEventProperties({ session, attempt: skippedAttempt, result }),
      });

      return undefined;
    },
    [session]
  );

  const skipOnce = useCallback(() => {
    const key = contextKey;
    const active = skipInFlight.current;
    if (active?.key === key) return active.promise;
    const canSkipFreshAttempt = !hasAttempt && preAttemptCanContinue === true;
    if (
      loading ||
      !isCurrentContext(key) ||
      !applicationId ||
      attemptContextKey !== key ||
      (!attempt.isOpen && !canSkipFreshAttempt)
    )
      return undefined;

    const request = skip()
      .then((response) => {
        if (!isCurrentContext(key) || !response) return response;

        if (response?.attempt?.get('status') === 'skipped') {
          setSkipState('skipped');
          emitSkipped(response.attempt, 'skipped');
          return response;
        }

        setSkipState('error');
        return response;
      })
      .catch((skipError) => {
        if (!isCurrentContext(key)) throw skipError;

        if (!isAmbiguousSkipError(skipError)) {
          setSkipState('error');
          throw skipError;
        }

        setSkipState('confirming');
        return refreshStatus()
          .then((response) => {
            if (!isCurrentContext(key) || !response) return response;

            if (response?.attempt?.get('status') === 'skipped') {
              setSkipState('skipped');
              emitSkipped(response.attempt, 'recovered');
              return response;
            }

            setSkipState('error');
            return response;
          })
          .catch((readbackError) => {
            if (!isCurrentContext(key)) throw readbackError;

            setSkipState('error');
            throw readbackError;
          });
      })
      .finally(() => {
        if (skipInFlight.current?.key === key) {
          skipInFlight.current = null;
        }
      });

    skipInFlight.current = { key, promise: request };
    return request;
  }, [
    applicationId,
    attempt,
    attemptContextKey,
    contextKey,
    emitSkipped,
    hasAttempt,
    isCurrentContext,
    loading,
    refreshStatus,
    skip,
  ]);

  useEffect(() => {
    if (
      attemptContextKey !== contextKey ||
      attempt.get('status') !== 'skipped' ||
      skipInFlight.current
    )
      return;

    setSkipState('skipped');
  }, [attempt, attemptContextKey, contextKey]);

  const creditLimit = useMemo(
    () =>
      attemptContextKey === contextKey
        ? baselineCreditLimit({ attempt, company })
        : null,
    [attempt, attemptContextKey, company, contextKey]
  );
  const continuation = useMemo(
    () =>
      attemptContextKey === contextKey &&
      phase === 'terminal' &&
      attempt.get('status') === 'skipped'
        ? continuationTarget({ attempt })
        : null,
    [attempt, attemptContextKey, contextKey, phase]
  );
  const continueToNextRoute = useCallback(
    (history) => {
      const matchesContext = () => {
        const sessions = store.getState().sessions;
        return isCurrentContext(contextKey) &&
          sessions.get('public_key') === publicKey &&
          sessions.getIn(['session', 'id']) === sessionId &&
          sessions.getIn(['session', 'application_id']) === applicationId;
      };
      if (!matchesContext() || phase === 'entity_refresh_error' || !continuation) return undefined;
      if (continuation.kind === 'path') return history.push(continuation.path);

      return dispatch(getRoute({ canApplyResponse: matchesContext }));
    },
    [continuation, dispatch, phase, store, isCurrentContext, contextKey, publicKey, sessionId, applicationId]
  );

  return {
    attempt,
    phase,
    preAttemptCanContinue,
    loading,
    error,
    skipState: attemptContextKey === contextKey ? skipState : 'idle',
    canSkip:
      attemptContextKey === contextKey &&
      (attempt.isOpen || (!hasAttempt && preAttemptCanContinue === true)),
    skipOnce,
    retrySkip: skipOnce,
    creditLimit,
    continuation,
    continueToNextRoute,
    retryEntities: refreshEntities,
  };
};

export default usePostApprovalPlaidSkip;
